import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const TABS = [
  {
    id: "whos-in",
    label: "Who's IN",
    image: "/assets/app-6.webp",
    eyebrow: "THE FEED",
    title: "See who's IN right now.",
    text: "Live moments from your city and beyond — Cooking in Stockholm, a set in Malmö. One tap on COME IN and you're there.",
  },
  {
    id: "live-room",
    label: "Live Room",
    image: "/assets/app-8.webp",
    eyebrow: "INSIDE AN IN",
    title: "Jump on. Tap to enter.",
    text: "People at the same place appear as green dots in the live. Tap a dot and you instantly jump into their view — same moment, new angle. React, capture, jump on.",
  },
  {
    id: "onboarding",
    label: "The Portal",
    image: "/assets/app-4.webp",
    eyebrow: "WHY IT EXISTS",
    title: "Be there. Together.",
    text: "Every moment is temporary. Being there is what stays. IN-It exists so you never have to just watch it happen.",
  },
];

export const AppPreviewSection = () => {
  const [active, setActive] = useState(TABS[0]);

  return (
    <section id="preview" className="relative py-28 sm:py-36 border-t border-white/5 overflow-hidden">
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full bg-ink-purple/10 blur-[160px] pointer-events-none" />
      <div className="relative max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-16 items-center">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8 }}
        >
          <p className="font-mono text-xs uppercase tracking-[0.3em] text-ink-coral mb-4">STRAIGHT FROM THE APP</p>
          <h2 className="font-display font-extrabold tracking-tighter text-white text-4xl sm:text-5xl mb-8">
            The real interface. <span className="text-brand-gradient">No mockups.</span>
          </h2>

          <div className="flex gap-2 mb-8" role="tablist">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                data-testid={`preview-tab-${tab.id}`}
                onClick={() => setActive(tab)}
                role="tab"
                aria-selected={active.id === tab.id}
                className={`rounded-full px-5 py-2.5 text-sm font-bold transition-all duration-200 ${
                  active.id === tab.id
                    ? "bg-gradient-to-r from-[#FF8A3D] to-[#FF3D3D] text-white"
                    : "border border-white/12 text-zinc-400 hover:text-white hover:border-white/30"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <AnimatePresence mode="wait">
            <motion.div
              key={active.id}
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.4 }}
            >
              <p className="font-mono text-[11px] tracking-[0.3em] text-ink-green mb-3">{active.eyebrow}</p>
              <h3 className="font-display font-bold text-white text-2xl sm:text-3xl tracking-tight mb-4">{active.title}</h3>
              <p className="text-zinc-400 leading-relaxed max-w-md">{active.text}</p>
            </motion.div>
          </AnimatePresence>
        </motion.div>

        <div className="relative flex justify-center">
          <AnimatePresence mode="wait">
            <motion.div
              key={active.id}
              initial={{ opacity: 0, rotateY: -18, y: 40 }}
              animate={{ opacity: 1, rotateY: 0, y: 0 }}
              exit={{ opacity: 0, rotateY: 14, y: -30 }}
              transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
              className="phone-frame w-[280px] sm:w-[320px]"
            >
              <div className="p-2.5 bg-black rounded-[2.5rem]">
                <div className="relative aspect-[9/17.6] overflow-hidden rounded-[2rem]">
                  <img src={active.image} alt={`IN-It app — ${active.label}`} className="absolute inset-0 w-full h-full object-cover" />
                  <div className="absolute top-2.5 left-1/2 -translate-x-1/2 w-24 h-[26px] bg-black rounded-full" />
                  <div className="pointer-events-none absolute inset-0 rounded-[2rem] shadow-[inset_0_0_28px_rgba(0,0,0,0.5)]" />
                </div>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
};
